import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import MixpanelService from "@/services/mixpanel";
import { useAuth } from '@/hooks/useAuth';

const Header: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isHomePage = window.location.pathname === "/join";
  const { user } = useAuth();

  const goToSection = (anchor: string) => {
    // Track button click
    MixpanelService.trackButtonClick(`Header Navigation - ${anchor}`, {
      section: anchor,
      currentPath: window.location.pathname
    });

    // Close mobile drawer if open
    setIsDrawerOpen(false);

    if (isHomePage) {
      // If on home page, scroll to the section
      const section = document.querySelector(`#${anchor}`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home page, navigate to home page with anchor
      window.location.href = `/#${anchor}`;
    }
  };

  const handleApplyAsTesterClick = () => {
    MixpanelService.trackButtonClick("Header Navigation - Apply as Tester", {
      currentPath: window.location.pathname
    });

    // Close mobile drawer if open
    setIsDrawerOpen(false);

    window.open("https://tastermonial.com/pages/applytotest", "_blank");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-neutral-100 w-full">
      <div className="container mx-auto flex items-center justify-between h-16 md:h-20 px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center">
          <div className="flex items-center mr-8">
            <img
              src="/images/logo-wide.png"
              alt="Tastermonial Logo"
              className="h-8 md:h-10 transform scale-150 translate-y-1"
              onClick={() => {
                MixpanelService.trackButtonClick("Logo Click");
                if (!isHomePage) {
                  window.location.href = "/";
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={() => goToSection("how-it-works")}
            className="text-sm font-medium text-neutral-700 hover:text-primary transition-colors"
          >
            How It Works
          </button>
          <button
            onClick={() => goToSection("pricing")}
            className="text-sm font-medium text-neutral-700 hover:text-primary transition-colors"
          >
            Pricing
          </button>
          <button
            onClick={() => goToSection("faq")}
            className="text-sm font-medium text-neutral-700 hover:text-primary transition-colors"
          >
            FAQ
          </button>
          <button
            onClick={handleApplyAsTesterClick}
            className="text-sm font-medium text-neutral-700 hover:text-primary transition-colors"
          >
            Apply as Tester
          </button>
        </nav>

        <div className="flex items-center gap-3">
          {/* Mobile Menu Trigger */}
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setIsDrawerOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="text-left">
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className="absolute right-4 top-4">
                    <X className="h-4 w-4" />
                  </Button>
                </DrawerClose>
              </DrawerHeader>
              <div className="px-4 pb-8">
                <nav className="flex flex-col gap-4">
                  <button
                    onClick={() => goToSection("how-it-works")}
                    className="text-left text-lg font-medium text-neutral-700 hover:text-primary transition-colors py-2"
                  >
                    How It Works
                  </button>
                  <button
                    onClick={() => goToSection("pricing")}
                    className="text-left text-lg font-medium text-neutral-700 hover:text-primary transition-colors py-2"
                  >
                    Pricing
                  </button>
                  <button
                    onClick={() => goToSection("faq")}
                    className="text-left text-lg font-medium text-neutral-700 hover:text-primary transition-colors py-2"
                  >
                    FAQ
                  </button>
                  <button
                    onClick={handleApplyAsTesterClick}
                    className="text-left text-lg font-medium text-neutral-700 hover:text-primary transition-colors py-2"
                  >
                    Apply as Tester
                  </button>
                  <div className="pt-4 border-t border-neutral-200">
                    <Button
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary-50"
                      onClick={() => {
                        MixpanelService.trackButtonClick(`Header CTA - ${user ? 'Dashboard' : 'Login'} (Mobile)`);
                        window.location.href = user ? '/dashboard' : '/login';
                      }}
                    >
                      {user ? 'Dashboard' : 'Login'}
                    </Button>
                  </div>
                </nav>
              </div>
            </DrawerContent>
          </Drawer>

          {/* Desktop CTA Button */}
          <Button
            size="sm"
            variant="outline"
            className="hidden md:block border-primary text-primary hover:bg-primary-50"
            onClick={() => {
              MixpanelService.trackButtonClick(`Header CTA - ${user ? 'Dashboard' : 'Login'}`);
              window.location.href = user ? '/dashboard' : '/login';
            }}
          >
            {user ? 'Dashboard' : 'Login'}
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
